'use client';

import { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageComponent } from '@/components/Image';
import type { IComparisonUICol, IComparisonUISection } from '../../types';
import { SlideColHeader } from './components/SlideColHeader';
import { SliderControls } from './components/SliderControls';
import { SlideSection } from './components/SlideSection';
import { useComparisonSlider } from './hooks/useComparisonSlider';
import {
  ScComparisonSlider,
  ScLabelCell,
  ScLabelColumn,
  ScSectionTitle,
  ScSlide,
  ScSlideLabelHeader,
  ScSliderLayout,
  ScSlidesList,
  ScSlideViewport,
  ScSwipeHint,
} from './styled';

interface IComparisonSliderProps {
  cols: IComparisonUICol[];
  sections: IComparisonUISection[];
  colsTitle: string;
}

export const ComparisonSlider = memo(
  ({ cols, sections, colsTitle }: IComparisonSliderProps) => {
    const { t } = useTranslation('vip');
    const {
      emblaRef,
      containerRef,
      cardRef,
      cardHeight,
      selectedIndex,
      slidesPerView,
      canPrev,
      canNext,
      scrollPrev,
      scrollNext,
    } = useComparisonSlider(cols.length);

    return (
      <ScComparisonSlider>
        <ScSwipeHint>
          <ImageComponent
            alt="swipe"
            height={24}
            src="/vip/icon-swipe.webp"
            width={24}
          />
          {t('steps.vipTransfer.comparisonSection.swipe')}
        </ScSwipeHint>

        <ScSliderLayout>
          <ScLabelColumn>
            <ScSlideLabelHeader style={{ height: cardHeight }}>
              {colsTitle}
            </ScSlideLabelHeader>
            {sections.map((section) => (
              <Fragment key={section.id}>
                <ScSectionTitle>{section.title}</ScSectionTitle>
                {section.rows.map((row) => (
                  <ScLabelCell key={row.id}>{row.label}</ScLabelCell>
                ))}
              </Fragment>
            ))}
          </ScLabelColumn>

          <ScSlideViewport ref={containerRef}>
            <div ref={emblaRef} style={{ overflow: 'hidden' }}>
              <ScSlidesList>
                {cols.map((col, colIndex) => (
                  <ScSlide $perView={slidesPerView} key={col.id}>
                    <SlideColHeader
                      col={col}
                      colIndex={colIndex}
                      ref={colIndex === 0 ? cardRef : undefined}
                    />
                    {sections.map((section) => (
                      <SlideSection
                        colIndex={colIndex}
                        key={section.id}
                        section={section}
                      />
                    ))}
                  </ScSlide>
                ))}
              </ScSlidesList>
            </div>
          </ScSlideViewport>
        </ScSliderLayout>

        <SliderControls
          canNext={canNext}
          canPrev={canPrev}
          currentIndex={selectedIndex}
          onNext={scrollNext}
          onPrev={scrollPrev}
          slidesPerView={slidesPerView}
          totalSlides={cols.length}
        />
      </ScComparisonSlider>
    );
  }
);

ComparisonSlider.displayName = 'ComparisonSlider';
